import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import candidateService from "@/services/candidateService";
import Loader from "@/components/Loader";
import { ArrowLeft } from "lucide-react";
import UniversityRepeatableField from "@/components/Frontend/components/UniversityRepeatableField";
import ApplicationView from "@/components/candidates/ApplicationView";

const CandidateApplicationView = () => {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCV, setShowCV] = useState(true);

  useEffect(() => {
    const fetchApplication = async () => {
      try {
        setIsLoading(true);
        const { data } =
          await candidateService.getCandidateApplicationById(applicationId);
        setApplication(data);
      } catch (err) {
        console.error("Error fetching application:", err);
        setError(err.message || "Failed to load application");
      } finally {
        setIsLoading(false);
      }
    };

    fetchApplication();
  }, [applicationId]);

  const renderFieldValue = (field) => {
    // Handle university_repeatable field type
    if (field.field_type === "university_repeatable") {
      return (
        <UniversityRepeatableField
          fieldIndex={0}
          field={{ label: field.field_name }}
          isRequired={false}
          displayMode={true}
          initialValue={field.field_value}
        />
      );
    }

    // Handle other field types
    return (
      <p className="font-medium bg-muted mt-1 rounded-lg p-2 py-3 text-foreground w-full">
        {field.field_value || "N/A"}
      </p>
    );
  };

  const goBack = () => {
    navigate("/candidate/applications");
  };

  if (isLoading) {
    return (
      <div className="w-[90%] mx-auto mt-10 mb-6">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-[90%] mx-auto mt-10 mb-6">
        <div className="bg-card rounded-xl shadow-xl p-8 max-w-md text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">
            Error Loading Application
          </h2>
          <p className="text-foreground mb-6">{error}</p>
          <button
            onClick={goBack}
            className="px-4 py-2 bg-primary text-white rounded-lg flex items-center gap-2 mx-auto"
          >
            <ArrowLeft size={16} />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="w-[90%] mx-auto mt-10 mb-6">
        <div className="bg-card rounded-xl shadow-xl p-8">
          <h2 className="text-2xl font-bold text-foreground mb-4">
            Application Not Found
          </h2>
          <p className="text-foreground mb-6">
            The application you're looking for doesn't exist or has been
            removed.
          </p>
          <button
            onClick={goBack}
            className="px-4 py-2 bg-primary-dark text-white rounded-lg flex items-center gap-2 mx-auto"
          >
            <ArrowLeft size={16} />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-[90%] mx-auto mt-10 mb-6 max-w-[1280px]">
      <div className="mb-6">
        <button
          onClick={goBack}
          className="bg-primary-dark hover:scale-105 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
        >
          <ArrowLeft size={16} />
          Back to Applications
        </button>
      </div>

      <ApplicationView
        currentApplication={application}
        currentVacancy={application.vacancy}
        tagsPerApplication={{
          [application.id]:
            application.application_tags?.map((tag) => tag.name) || [],
        }}
        showCV={showCV}
        onToggleCV={() => setShowCV((prev) => !prev)}
        onShowHistory={() => {}}
      />
    </div>
  );
};

export default CandidateApplicationView;
